import { type Editor } from '@tiptap/react';

/**
 * Finds and returns the currently open Radix dialog container,
 * or the editor's parent container as a fallback.
 *
 * This is used to ensure BubbleMenu and other floating elements
 * are appended to the correct container when inside a dialog.
 *
 * `fallbackToBody` switches the non-dialog fallback from the editor's parent
 * (which lives inside the note column's stacking context — clipped/painted
 * behind the side panel) to `document.body`. The selection AI-actions menu
 * needs this so the whole floating panel — and its inline sub-flyouts — paint
 * above the side panel. Keeping the sub-flyouts inline (no portal of their own)
 * then lets Radix anchor them to their trigger instead of snapping to (0,0).
 *
 * `fallbackElement` replaces the editor's parent as the non-dialog target —
 * e.g. the parent of a `contain: layout` scroller, whose stacking context
 * would otherwise cap the menu's z-index.
 */
export const getDialogContainer = (
  editor: Editor,
  {
    fallbackToBody = false,
    fallbackElement,
  }: { fallbackToBody?: boolean; fallbackElement?: HTMLElement | null } = {}
): HTMLElement => {
  // Look for Radix dialog content (various possible selectors)
  const dialogContent =
    document.querySelector('[data-radix-dialog-content]') ||
    document.querySelector('[role="dialog"]') ||
    document.querySelector('[data-state="open"][data-radix-popper-content-wrapper]');

  if (dialogContent) {
    return dialogContent as HTMLElement;
  }

  if (fallbackToBody) return document.body;

  // If no dialog, append to editor's parent container
  return fallbackElement || editor.view.dom.parentElement || document.body;
};
